import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  AfterViewInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  Renderer2,
  SimpleChanges,
  HostListener,
  OnInit,
  ChangeDetectorRef
} from '@angular/core';
import { CommonModule } from '@angular/common';

import { NgxMasonryModule } from 'ngx-masonry';
import { fromEvent, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, first } from 'rxjs/operators';

import { CreatorService } from 'app/shared/services/creator.service';
import { ConversationService } from 'app/shared/services/conversation.service';
import { TokensService } from 'app/shared/services/tokens.service';
import { AnalyticsService } from '../../shared/services/analytics.service';

import { EmptyStateComponent } from '../empty-state/empty-state.component';
import { MasonryGalleryComponent } from 'app/shared/components/masonry-gallery/masonry-gallery.component';
import { SvgIconComponent } from 'app/shared/components/svg-icon/svg-icon.component';
import { MediaLightboxComponent } from '../media-lightbox/media-lightbox.component';
import { ImageComponent } from '../../shared/components/image/image.component';

import { ImageProtectionDirective } from 'app/shared/directives/image-protection.directive';

import { ISvgConfig } from 'app/shared/interfaces/svg.interfaces';
import {
  IConversationHistoryResponse,
  IConversationSharedMediaResponse,
  IMessage
} from 'app/interfaces/conversations.interfaces';
import {
  ICreatorContent,
  ICreatorData,
  ICreatorDiscoverData
} from 'app/interfaces/creator.interfaces';
import { IEmptyStateConfig } from '../../shared/interfaces/shared.interfaces';

import { ESvgTypes } from 'app/shared/enums/svg.enums';
import { EFriendshipTab } from 'app/shared/enums/friendship.enums';
import { EMediaLightboxUsage } from '../../shared/enums/shared.enums';
import { ECreatorMediaTypes } from 'app/enums/creator.enums';
import { EImageUsage } from 'app/shared/enums/image-usage.enums';

@Component({
  selector: 'stxt-friendship-panel',
  standalone: true,
  imports: [
    CommonModule,
    NgxMasonryModule,
    EmptyStateComponent,
    MasonryGalleryComponent,
    SvgIconComponent,
    MediaLightboxComponent,
    ImageComponent,
    ImageProtectionDirective
  ],
  templateUrl: './friendship-panel.component.html',
  styleUrl: './friendship-panel.component.scss'
})
export class FriendshipPanelComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
  @Input() friendsSince: Date;
  @Input() isFriendshipOpen: boolean;
  @Input() creatorData: ICreatorDiscoverData;
  @Output() closeFriendshipPage: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() requestPhotos: EventEmitter<void> = new EventEmitter<void>();
  @ViewChild('parallaxImage') parallaxImage: ElementRef;
  @ViewChild('parallaxContainer') parallaxContainer: ElementRef;

  public selectedTab: EFriendshipTab = EFriendshipTab.Profile;
  public EFriendshipTab = EFriendshipTab;
  public creatorProfile: ICreatorData;
  public sharedMedia: IConversationSharedMediaResponse;
  private scrollSubscription: Subscription;
  private newOwnedMediaSubscription: Subscription;
  private tokenPurchaseSubscription: Subscription;
  public ageIcon: ISvgConfig = { name: 'birthday', fill: ESvgTypes.None };
  public heightIcon: ISvgConfig = { name: 'height', fill: ESvgTypes.None };
  public profileIcon: ISvgConfig = { name: 'profile', fill: ESvgTypes.None };
  public timelineIcon: ISvgConfig = { name: 'favorite', fill: ESvgTypes.None };
  public ethnicityIcon: ISvgConfig = { name: 'ethnicity', fill: ESvgTypes.None };
  public closeIcon: ISvgConfig = { name: 'close', fill: ESvgTypes.Tonal };
  public playIcon: ISvgConfig = { name: 'play', fill: ESvgTypes.None };
  public userId: string = localStorage.getItem('userId');
  public ticking: boolean = false;
  public offset: string;
  public emptyStateData: IEmptyStateConfig = {
    svgName: 'friendship-timeline',
    heading: 'Make memories together',
    subheading: `See all your shared moments and content with `,
    buttonText: 'Request photos'
  };
  public mediaFullScreen: boolean = false;
  public selectedMedia: ICreatorContent;
  public EMediaLightboxUsage = EMediaLightboxUsage;
  private processedMediaIds: Set<string> = new Set();
  protected readonly EImageUsage = EImageUsage;

  constructor(
    private readonly creatorService: CreatorService,
    private readonly elementRef: ElementRef,
    private readonly renderer: Renderer2,
    private readonly conversationService: ConversationService,
    private readonly tokensService: TokensService,
    private readonly cdr: ChangeDetectorRef,
    private readonly analyticsService: AnalyticsService
  ) {}

  ngOnInit(): void {
    this.emptyStateData.subheading = this.emptyStateData.subheading + this.creatorData?.name;
    this.subscribeToTokenPurchase();
    this.listenToOwnedMedia();
  }

  ngAfterViewInit(): void {
    this.initScrollListener();
  }

  /**
   * Handles changes to component inputs.
   * Resets and updates shared media when creator data changes.
   * Updates friendsSince date when changed.
   * @param changes - Object containing changed properties
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['creatorData']?.currentValue?.id) {
      this.sharedMedia = {
        media: [],
        total: 0,
        offset: ''
      }; //reset shared media when creator data changes
      this.getCreatorData(changes['creatorData']?.currentValue?.id);
      this.getConversationSharedMedia(changes['creatorData']?.currentValue?.id);
    }
    if (changes['friendsSince']) {
      this.friendsSince = changes['friendsSince']?.currentValue || new Date();
      this.cdr.detectChanges();
    }
  }

  /**
   * Fetches detailed creator profile data.
   * @param id - Creator's unique identifier
   */
  getCreatorData(id: string): void {
    this.creatorService
      .getCreatorData(id)
      .pipe(first())
      .subscribe((creatorProfile: ICreatorData) => {
        this.creatorProfile = creatorProfile;
      });
  }

  checkHistoryItem(message: IMessage): void {
    // TODO finish logic for adding images from history message update
    if (this.sharedMedia.media.some(m => m.value === message.payload?.files?.[0]?.url)) {
      return;
    }
  }

  /**
   * Retrieves shared media for a conversation with a creator.
   * Maps vault_id to media_id and initiates image preloading.
   * @param id - Creator's unique identifier
   */
  getConversationSharedMedia(id: string): void {
    this.conversationService
      .getConversationSharedMedia(id)
      .pipe(first())
      .subscribe((mediaData: IConversationSharedMediaResponse) => {
        if (!mediaData.media) return;
        this.sharedMedia = {
          media: mediaData.media.map(media => ({
            ...media,
            media_id: media.vault_id
          })),
          total: mediaData.total,
          offset: mediaData.offset
        };
      });
  }

  selectTab(tab: EFriendshipTab): void {
    this.selectedTab = tab;
  }

  /**
   * Host listener for scroll events.
   * Implements request animation frame pattern to optimize parallax effect updates.
   */
  @HostListener('scroll', ['$event'])
  onScroll(): void {
    if (!this.ticking) {
      globalThis.requestAnimationFrame(() => {
        this.updateParallaxEffect();
        this.ticking = false;
      });
      this.ticking = true;
    }
  }

  /**
   * Sets up a debounced scroll event listener for updating parallax effects.
   * Optimizes performance by reducing the frequency of updates.
   */
  private initScrollListener(): void {
    this.scrollSubscription = fromEvent(this.elementRef.nativeElement, 'scroll')
      .pipe(debounceTime(10), distinctUntilChanged())
      .subscribe(() => {
        this.updateParallaxEffect();
      });
  }

  /**
   * Calculates and applies parallax scrolling effects.
   * Updates opacity, blur, and gradient based on scroll position.
   */
  private updateParallaxEffect(): void {
    const scrollPosition = this.elementRef.nativeElement.scrollTop;
    const maxScroll = 300;
    const progress = Math.min(scrollPosition / maxScroll, 1);
    const startOpacity = 0.2;
    const endOpacity = 0.8;
    const opacity = startOpacity + progress * (endOpacity - startOpacity);
    const blur = progress * 10;
    const gradient = `radial-gradient(50% 50% at 50% 50%, rgba(26, 20, 26, ${opacity}) 0%, rgba(26, 20, 26, 1) 120%)`;

    if (this.parallaxImage?.nativeElement) {
      this.renderer.setStyle(this.parallaxImage.nativeElement, 'filter', `blur(${blur}px)`);
      this.renderer.setStyle(this.parallaxImage.nativeElement, 'opacity', 1 - progress);
    }

    if (this.parallaxContainer?.nativeElement) {
      this.renderer.setStyle(this.parallaxContainer.nativeElement, '--parallax-gradient', gradient);
    }
  }

  /**
   * Updates shared media collection with new media.
   * Prevents duplicates and maintains media list structure.
   * @param mediaData - Message containing new media data
   */
  private updateSharedMedia(mediaData: IMessage): void {
    const mediaUrl = mediaData.payload?.files?.[0]?.url || '';
    const timestamp = mediaData.timestamp || Date.now();

    // Check for duplicates using URL since it's unique
    if (
      !mediaUrl ||
      this.processedMediaIds.has(mediaUrl) ||
      this.sharedMedia?.media?.some(m => m.value === mediaUrl)
    ) {
      return;
    }

    this.processedMediaIds.add(mediaUrl);

    // Create a unique ID for the media using timestamp and URL
    const uniqueId = `${timestamp}-${mediaUrl.split('/').pop()}`;

    const newMedia = {
      timestamp: timestamp,
      value: mediaUrl,
      type: 'image',
      media_id: uniqueId,
      vault_id: uniqueId,
      unlocked_at: Date.now()
      // Remove isRendered and isError since MediaLoaderComponent handles these
    };

    // Sort media: unlocked first (newest to oldest), then historical (oldest to newest)
    const updatedMedia = [newMedia, ...(this.sharedMedia?.media || [])].sort((a, b) => {
      // If both items have unlocked_at, sort by unlocked_at (most recent first)
      if (a.unlocked_at && b.unlocked_at) {
        return b.unlocked_at - a.unlocked_at;
      }
      // If only one has unlocked_at, it should come first
      if (a.unlocked_at) return -1;
      if (b.unlocked_at) return 1;

      // For historical images (no unlocked_at), sort by timestamp (oldest first)
      const timestampA =
        typeof a.timestamp === 'number' && !Number.isNaN(a.timestamp) ? a.timestamp : 0;
      const timestampB =
        typeof b.timestamp === 'number' && !Number.isNaN(b.timestamp) ? b.timestamp : 0;
      return timestampA - timestampB; // Changed to ascending order for historical images
    });

    this.sharedMedia = {
      ...this.sharedMedia,
      media: updatedMedia,
      total: (this.sharedMedia?.total || 0) + 1
    };

    this.cdr.detectChanges();
  }

  /**
   * Opens media lightbox for full-screen view.
   * Tracks media click event in analytics.
   * @param selectedMedia - Media content to display
   */
  public openMediaLightbox(selectedMedia: ICreatorContent): void {
    this.selectedMedia = selectedMedia;
    this.mediaFullScreen = true;

    this.analyticsService.mediaClicked(
      this.userId,
      this.creatorProfile.name,
      '',
      this.creatorProfile.id,
      selectedMedia.media_id,
      ECreatorMediaTypes.Image
    );
  }

  /**
   * Subscribes to new owned media updates.
   * Updates shared media collection when new media is received.
   */
  public listenToOwnedMedia(): void {
    this.newOwnedMediaSubscription = this.conversationService.newOwnedMedia$.subscribe(
      (newMessage: IMessage) => {
        if (!newMessage || !newMessage.timestamp || typeof newMessage.timestamp !== 'number')
          return;

        const mediaUrl = newMessage.payload?.files?.[0]?.url || '';
        if (!mediaUrl || this.processedMediaIds.has(mediaUrl)) return;

        this.updateSharedMedia(newMessage);
      }
    );
  }

  /**
   * Subscribes to token purchase events.
   * Updates shared media when new media is unlocked through token purchases.
   */
  private subscribeToTokenPurchase(): void {
    this.tokenPurchaseSubscription = this.tokensService.getMediaAfterTokenPurchase$.subscribe(
      (success: IConversationHistoryResponse) => {
        if (!success || !success.messages?.length) return;

        const message = success.messages[0];
        if (!message || !message.timestamp || typeof message.timestamp !== 'number') return;

        const mediaUrl = message.payload?.files?.[0]?.url || '';
        if (!mediaUrl || this.processedMediaIds.has(mediaUrl)) return;

        this.updateSharedMedia(message);
      }
    );
  }

  ngOnDestroy(): void {
    this.scrollSubscription?.unsubscribe();
    this.newOwnedMediaSubscription?.unsubscribe();
    this.tokenPurchaseSubscription?.unsubscribe();
  }
}
